// Trimmed port: the class-name helpers (`cn`, `interactiveText`) and their
// clsx / tailwind-merge deps were dropped — the toolbox report UI is MUI, not
// Tailwind. Everything below (the colour / severity model) is verbatim.

export function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

// --- Configurable color / severity model -----------------------------------
// The score color coding and severity thresholds are data-driven so the admin
// GUI can tune them at runtime. `makeColorFns(cfg)` builds the family of
// functions from a config; the module-level exports below bind the built-in
// defaults so existing call sites keep the exact prior behavior.

export type Severity = "Critical" | "High" | "Medium" | "Low";
export type RGB = [number, number, number];
export type HeatmapStop = [number, RGB];
export type SeverityThresholds = { critical: number; high: number; medium: number };

/**
 * V4.3 — the heatmap palette is now keyed on the *band a colour belongs to*,
 * not on a hand-set position along the 0–1 axis.
 *
 * The bug this fixes: the stops used to be stored as `[position, rgb]` pairs
 * with amber at 0.33 — but 0.33 *is* `thresholds.high`, so the instant a score
 * became High it was painted the colour that means Medium. A score of 52 (raw
 * 0.345, comfortably High) rendered yellow, while the High badge beside it
 * rendered red. The positions were set when the cutoffs were 0.55/0.40/0.20 and
 * were never re-anchored when V4.1 swept them to 0.57/0.33/0.15.
 *
 * Positions are therefore derived from the thresholds and are no longer
 * storable. `low` is the colour the gradient starts at (`lowThreshold`, below
 * which a cell is a flat var), and each of `medium`/`high`/`critical` is
 * the colour a band *enters at* — so `heatmapColor(thresholds.high)` is exactly
 * the red the High badge uses, by construction rather than by two sets of
 * numbers kept in sync by hand. An administrator who moves a cutoff drags its
 * colour with it. `max` is the colour at 1.0, so the top of the range keeps
 * darkening instead of flat-lining.
 */
export type BandPalette = {
  low: RGB;
  medium: RGB;
  high: RGB;
  critical: RGB;
  max: RGB;
};

export type ColorConfig = {
  thresholds: SeverityThresholds;
  palette: BandPalette;
  lowThreshold: number;
  /**
   * Legacy, read-only. Pre-V4.3 overrides persisted positioned stops. They are
   * accepted for their *colours*, in order, and their positions are discarded —
   * a saved override must not be able to reintroduce the bug it is being fixed
   * for. Never write this field.
   */
  stops?: HeatmapStop[];
};

// Built-in defaults — recalibrated for the FAIR model. Because Risk =
// Likelihood × Impact (a product of two 0–1 terms), scores compress downward
// versus the old single-exposure model, so the bands sit lower.
//
// V4.1: re-set against the observed distribution over the 50-scenario corpus in
// `tests/` (see FINDINGS.md F-01). The previous cutoffs (0.55/0.40/0.20) put
// `high` at the corpus p75 and `critical` above p90, so at most a quarter of
// realistic organizations could ever read High or worse — 27 of 50 landed in
// Medium where an expert assessor placed 18. These values were chosen by
// sweeping all three cutoffs against the assessor verdicts and taking the
// centre of the plateau that maximizes exact band agreement (34/50, up from
// 29/50; total band distance 16, down from 21). Threshold tuning alone cannot
// do better than 34/50 — the residual error needs the threat-ranking and
// `data_sources` work (F-07, F-10), not a different cutoff.
// V4.3: the four band colours are the same hexes the severity badges
// (`ui/primitives/badge.tsx`, vars in `globals.css`) and the PDF palette
// (`pdf/template/styles.ts`) already use, so the gradient and the label agree
// at every boundary. `max` is new — everything above raw 0.70 used to clamp to
// one identical dark red, which made an 82 and a 98 indistinguishable.
export const DEFAULT_BAND_PALETTE: BandPalette = {
  low: [146, 208, 80], // #92D050 light green — Low badge
  medium: [255, 192, 0], // #FFC000 amber — Medium badge
  high: [255, 0, 0], // #FF0000 red — High badge
  critical: [192, 0, 0], // #C00000 dark red — Critical badge
  max: [128, 0, 0], // #800000 maroon — top of the range
};

export const DEFAULT_COLOR_CONFIG: ColorConfig = {
  thresholds: { critical: 0.57, high: 0.33, medium: 0.15 },
  palette: DEFAULT_BAND_PALETTE,
  lowThreshold: 0.05,
};

/**
 * Band colours → positioned gradient stops. The only place positions exist.
 *
 * The Low anchor sits at `lowThreshold`, not at 0: everything below it is
 * painted a flat `--cell-low`, so a stop at 0 would spend the whole *drawn* Low
 * range (0.05–0.15) already blending toward amber, and the Low colour itself
 * would never appear on screen. Anchoring at the first raw value the gradient
 * actually draws makes `heatmapColor(bandStart) === bandColor` true for all
 * four bands rather than three.
 */
function stopsFrom(t: SeverityThresholds, p: BandPalette, lowThreshold: number): HeatmapStop[] {
  return [
    [Math.min(lowThreshold, t.medium), p.low],
    [t.medium, p.medium],
    [t.high, p.high],
    [t.critical, p.critical],
    [1, p.max],
  ];
}

let warnedLegacyStops = false;

/**
 * Accepts a pre-V4.3 override that stored `stops: [[pos, rgb], …]`: the colours
 * are taken in order and the positions dropped. Anything missing falls back to
 * the default for that band, so a 4-stop legacy override still gets a `max`.
 */
function resolvePalette(cfg: ColorConfig): BandPalette {
  if (cfg.palette) return cfg.palette;
  const legacy = cfg.stops;
  if (!Array.isArray(legacy) || legacy.length === 0) return DEFAULT_BAND_PALETTE;
  if (!warnedLegacyStops && typeof console !== "undefined") {
    warnedLegacyStops = true;
    console.warn(
      "[colors] legacy positioned `stops` override detected — using its colours, " +
        "discarding its positions. Re-save from admin › Report Colors to migrate.",
    );
  }
  const rgb = legacy.map((s) => s?.[1]).filter((c): c is RGB => Array.isArray(c) && c.length === 3);
  const at = (i: number, fallback: RGB): RGB => rgb[i] ?? fallback;
  const critical = at(3, DEFAULT_BAND_PALETTE.critical);
  return {
    low: at(0, DEFAULT_BAND_PALETTE.low),
    medium: at(1, DEFAULT_BAND_PALETTE.medium),
    high: at(2, DEFAULT_BAND_PALETTE.high),
    critical,
    // A 4-stop legacy override never chose a colour for 1.0; hold its darkest
    // rather than inventing one.
    max: at(4, critical),
  };
}

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function toHex2(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();
}

function interpolate(stops: HeatmapStop[], e: number): RGB {
  for (let i = 0; i < stops.length - 1; i++) {
    const [p1, c1] = stops[i];
    const [p2, c2] = stops[i + 1];
    if (e <= p2) {
      const t = p2 === p1 ? 0 : (e - p1) / (p2 - p1);
      return [
        lerpChannel(c1[0], c2[0], t),
        lerpChannel(c1[1], c2[1], t),
        lerpChannel(c1[2], c2[2], t),
      ];
    }
  }
  return stops[stops.length - 1][1];
}

export type ColorFns = {
  severity: (exposure: number) => Severity;
  /** Raw 0..1 exposure → the 0..100 number shown to the user. See SCORE_ANCHORS. */
  toScore100: (risk: number | null | undefined) => number;
  heatmapColor: (exposure: number | null) => string;
  heatmapTextColor: (exposure: number | null) => string;
  heatmapHex: (exposure: number | null) => string;
  heatmapTextHex: (exposure: number | null) => string;
  /**
   * V4.3 — the flat colour of a band, for legends, strips and swatches that
   * label a *band* rather than plot a value. Equals `heatmapColor(threshold)`
   * for that band by construction, so a legend can never disagree with the
   * cells it is explaining.
   */
  bandColor: (band: Severity) => string;
  /** The raw exposure at which a band begins — Low begins at 0. */
  bandStart: (band: Severity) => number;
};

/**
 * V4.2 — the user-facing 0–100 calibration.
 *
 * The engine works in raw exposure, 0..1, and always will: it is a FAIR product
 * of two sub-1 terms, so it is structurally confined to the lower half of its
 * own range. Across the 50-scenario corpus the raw score spanned 0.05–0.78 with
 * a median of 0.33, and the band cutoffs sat at 0.15 / 0.33 / 0.57 — invisible
 * numbers. "33 out of 100 — Medium" is not a sentence a customer can act on.
 *
 * So the *display* is calibrated by a piecewise-linear map anchored on the band
 * thresholds themselves:
 *
 *     0 -> 0     medium threshold -> 25
 *     high -> 50     critical -> 75     1.0 -> 100
 *
 * The justification is one sentence with no free parameters: **25 is where
 * Medium starts, 50 is where High starts, 75 is where Critical starts.** A
 * score of 62 sits just under two-thirds of the way from High to Critical. The
 * number and the band label can never disagree, because they are derived from
 * the same four numbers — and an administrator who moves a threshold moves the
 * calibration with it rather than desyncing the two.
 *
 * Two consequences worth stating out loud in the methodology:
 *  - The map is monotonic, so every ordering the engine produces survives it —
 *    including `overall <= max(threat exposure)`, which is visible on screen.
 *  - It is steepest at the bottom, so the same raw improvement is worth more
 *    points to a lower-risk organisation. That is correct — the Low and Medium
 *    bands are genuinely narrower in raw terms — but it reads as a bug if the
 *    report does not say it.
 */
export const SCORE_ANCHORS = [0, 25, 50, 75, 100] as const;

function scoreKnots(thresholds: ColorConfig["thresholds"]): Array<[number, number]> {
  return [
    [0, 0],
    [thresholds.medium, 25],
    [thresholds.high, 50],
    [thresholds.critical, 75],
    [1, 100],
  ];
}

/** Band → its anchor colour in a palette. The four severity labels, in order. */
export const SEVERITY_ORDER: readonly Severity[] = ["Low", "Medium", "High", "Critical"] as const;
const BAND_RGB: Record<Severity, (p: BandPalette) => RGB> = {
  Low: (p) => p.low,
  Medium: (p) => p.medium,
  High: (p) => p.high,
  Critical: (p) => p.critical,
};

export function makeColorFns(cfg: ColorConfig = DEFAULT_COLOR_CONFIG): ColorFns {
  const { thresholds, lowThreshold } = cfg;
  const palette = resolvePalette(cfg);
  const stops = stopsFrom(thresholds, palette, lowThreshold);
  const knots = scoreKnots(thresholds);
  // A hand-edited override could put the thresholds out of order or on top of
  // each other, which would make the map non-monotonic (or divide by zero).
  // Rather than emit nonsense, fall back to the plain percentage.
  const knotsValid = knots.every(([x], i) => i === 0 || x > knots[i - 1][0]);
  return {
    severity(exposure: number): Severity {
      if (exposure >= thresholds.critical) return "Critical";
      if (exposure >= thresholds.high) return "High";
      if (exposure >= thresholds.medium) return "Medium";
      return "Low";
    },
    toScore100(risk: number | null | undefined): number {
      const r = clamp01(typeof risk === "number" && Number.isFinite(risk) ? risk : 0);
      if (!knotsValid) return Math.round(r * 100);
      let raw = 100;
      for (let i = 0; i < knots.length - 1; i++) {
        const [x1, y1] = knots[i];
        const [x2, y2] = knots[i + 1];
        if (r <= x2) {
          raw = y1 + ((r - x1) / (x2 - x1)) * (y2 - y1);
          break;
        }
      }
      // Rounding alone can push a value that sits just *below* a threshold onto
      // the anchor above it — 0.1499 raw is Low but rounds to 25, which the
      // reader would take as Medium. Clamp the rounded number into the integer
      // range its own band owns, so "score >= 25 iff Medium" holds exactly.
      const s = Math.round(raw);
      if (r >= thresholds.critical) return Math.max(75, s);
      if (r >= thresholds.high) return Math.min(74, Math.max(50, s));
      if (r >= thresholds.medium) return Math.min(49, Math.max(25, s));
      return Math.min(24, s);
    },
    heatmapColor(exposure: number | null): string {
      if (exposure === null) return "var(--cell-empty)";
      const e = clamp01(exposure);
      if (e < lowThreshold) return "var(--cell-low)";
      const [r, g, b] = interpolate(stops, e);
      return `rgb(${r}, ${g}, ${b})`;
    },
    heatmapTextColor(exposure: number | null): string {
      if (exposure === null) return "rgba(7, 24, 45, 0.4)"; // midnight blue at 40%
      // White once the cell is dark enough to need it. V4.3: keyed on the
      // Critical cutoff rather than a hardcoded 0.6, which sat *above* the
      // cutoff and so put midnight-blue text on dark red between 0.57 and 0.60.
      return exposure >= thresholds.critical ? "white" : "#07182d";
    },
    // Plain "RRGGBB" for DOCX shading.fill and @react-pdf backgroundColor.
    heatmapHex(exposure: number | null): string {
      if (exposure === null) return "ECEFF1"; // empty / not-mapped cell
      const e = clamp01(exposure);
      if (e < lowThreshold) return "E9F3DC"; // very light green
      const [r, g, b] = interpolate(stops, e);
      return toHex2(r) + toHex2(g) + toHex2(b);
    },
    heatmapTextHex(exposure: number | null): string {
      if (exposure === null) return "525E6C";
      return clamp01(exposure) >= thresholds.critical ? "FFFFFF" : "07182D";
    },
    bandColor(band: Severity): string {
      const [r, g, b] = BAND_RGB[band](palette);
      return `rgb(${r}, ${g}, ${b})`;
    },
    bandStart(band: Severity): number {
      return band === "Critical"
        ? thresholds.critical
        : band === "High"
          ? thresholds.high
          : band === "Medium"
            ? thresholds.medium
            : 0;
    },
  };
}

// Back-compat: default-bound exports keep every existing import working.
const DEFAULT_FNS = makeColorFns(DEFAULT_COLOR_CONFIG);
export const severity = DEFAULT_FNS.severity;
export const toScore100 = DEFAULT_FNS.toScore100;
export const heatmapColor = DEFAULT_FNS.heatmapColor;
export const heatmapTextColor = DEFAULT_FNS.heatmapTextColor;
export const heatmapHex = DEFAULT_FNS.heatmapHex;
export const heatmapTextHex = DEFAULT_FNS.heatmapTextHex;
export const bandColor = DEFAULT_FNS.bandColor;
export const bandStart = DEFAULT_FNS.bandStart;
